import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { BookMarked, Lock } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { notificar } from '@/shared/ui/avisos';
import {
  CampoDelPanel,
  DosColumnas,
  PanelLateral,
  claseEntradaPanel,
} from '@/shared/ui/PanelLateral';
import { mensajeDe } from './mensajes';
import type { EntradaDeCatalogo } from './contrato';

export function PanelDeLaEntrada({
  catalogo,
  catalogoEtiqueta,
  entrada,
  alCerrar,
  alGuardar,
}: {
  catalogo: string;
  catalogoEtiqueta: string;
  entrada: EntradaDeCatalogo | null;
  alCerrar: () => void;
  alGuardar: () => void;
}) {
  const editando = entrada !== null;
  const [codigo, setCodigo] = useState(entrada?.codigo ?? '');
  const [etiqueta, setEtiqueta] = useState(entrada?.etiqueta ?? '');
  const [orden, setOrden] = useState(String(entrada?.orden ?? 0));
  const [activo, setActivo] = useState(entrada?.activo ?? true);
  const [nota, setNota] = useState(entrada?.nota ?? '');
  const [error, setError] = useState<string | null>(null);

  const guardar = useMutation({
    mutationFn: () => {
      const cuerpo = {
        codigo: codigo.trim(),
        etiqueta: etiqueta.trim(),
        orden: Number(orden) || 0,
        activo,
        nota: nota.trim() || null,
      };
      return editando
        ? api.put<EntradaDeCatalogo>(`/compras/catalogos/entradas/${entrada.id}`, cuerpo)
        : api.post<EntradaDeCatalogo>(`/compras/catalogos/${catalogo}`, cuerpo);
    },
    onSuccess: () => {
      notificar.exito(editando ? 'Entrada actualizada' : 'Entrada creada');
      alGuardar();
    },
    onError: (causa) => {
      const mensaje = mensajeDe(causa, 'No se pudo guardar');
      setError(mensaje);
      notificar.error(mensaje);
    },
  });

  const falta =
    codigo.trim() === ''
      ? 'Falta el código'
      : etiqueta.trim() === ''
        ? 'Falta cómo se llama'
        : null;

  return (
    <PanelLateral
      abierto
      alCerrar={alCerrar}
      icono={BookMarked}
      titulo={editando ? 'Editar la entrada' : 'Nueva entrada'}
      descripcion={catalogoEtiqueta}
      alEnviar={(evento) => {
        evento.preventDefault();
        if (falta === null) {
          guardar.mutate();
        }
      }}
      enviando={guardar.isPending}
    >
      <div className="space-y-5">
        <DosColumnas>
          <CampoDelPanel
            etiqueta="Código"
            requerido
            ayuda={editando ? 'No se cambia: viaja en el ATS' : 'El del SRI'}
          >
            <input
              value={codigo}
              onChange={(e) => setCodigo(e.target.value)}
              maxLength={10}
              disabled={editando}
              className={claseEntradaPanel}
            />
          </CampoDelPanel>

          <CampoDelPanel etiqueta="Orden" ayuda="Para que salga arriba lo que más se usa">
            <input
              type="number"
              value={orden}
              onChange={(e) => setOrden(e.target.value)}
              className={claseEntradaPanel}
            />
          </CampoDelPanel>
        </DosColumnas>

        <CampoDelPanel etiqueta="Cómo se llama" requerido>
          <input
            value={etiqueta}
            onChange={(e) => setEtiqueta(e.target.value)}
            maxLength={160}
            className={claseEntradaPanel}
          />
        </CampoDelPanel>

        <CampoDelPanel etiqueta="Nota" ayuda="Qué la distingue, cuando el nombre no basta">
          <textarea
            value={nota}
            onChange={(e) => setNota(e.target.value)}
            maxLength={300}
            rows={2}
            className={`${claseEntradaPanel} h-auto py-2`}
          />
        </CampoDelPanel>

        <label className="flex items-center gap-2 text-xs font-semibold uppercase text-armazon-700">
          <input
            type="checkbox"
            checked={activo}
            onChange={(e) => setActivo(e.target.checked)}
            className="size-4 rounded border-armazon-300"
          />
          Se ofrece en los desplegables
        </label>

        {entrada?.delSistema && (
          <p className="flex gap-3 rounded-xl border border-armazon-200 bg-armazon-50/60 p-4 text-justify text-[11px] uppercase leading-relaxed text-armazon-600">
            <Lock className="mt-0.5 size-4 shrink-0" aria-hidden />
            <span>
              El sistema sabe tratar esta entrada, así que no se borra. Si no quiere que se
              ofrezca, desmárquela: lo ya registrado con ella seguirá enseñándose bien.
            </span>
          </p>
        )}

        {(error ?? falta) && (
          <p
            role={error ? 'alert' : undefined}
            className={`text-[11px] font-semibold uppercase ${
              error ? 'text-red-700' : 'text-armazon-500'
            }`}
          >
            {error ?? falta}
          </p>
        )}
      </div>
    </PanelLateral>
  );
}
